import { ChevronDown, Search } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { OrgCR } from '@/types/organization'

interface CRMultiSelectorProps {
	crs: OrgCR[]
	selected: string[]
	onChange: (selected: string[]) => void
	/** All CR IDs already linked to *other* contracts */
	usedCrIds: Set<string>
	/** ID of the contract currently being edited (its own CRs are not "used") */
	currentContractId?: string
}

export function CRMultiSelector({
	crs,
	selected,
	onChange,
	usedCrIds,
}: CRMultiSelectorProps) {
	const { t } = useTranslation()
	const [open, setOpen] = useState(true)
	const [search, setSearch] = useState('')

	const filtered = search.trim()
		? crs.filter(
				(cr) =>
					cr.name.toLowerCase().includes(search.trim().toLowerCase()) ||
					(cr.code ?? '').toLowerCase().includes(search.trim().toLowerCase()),
			)
		: crs

	const allSelected = selected.length === crs.length && crs.length > 0

	const handleToggleAll = () => {
		onChange(allSelected ? [] : crs.map((cr) => cr.id))
	}

	const handleToggle = (id: string) => {
		onChange(
			selected.includes(id)
				? selected.filter((s) => s !== id)
				: [...selected, id],
		)
	}

	return (
		<div className="rounded-lg border border-kaefer-light-silver/60 overflow-hidden">
			{/* Header */}
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				className="flex w-full cursor-pointer items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors"
			>
				<div className="flex items-center gap-2">
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{t('administration.contracts.form.crsTitle')}
					</span>
					{selected.length > 0 && (
						<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-kaefer-medium-grey/20 px-1.5 text-[11px] font-semibold tabular-nums text-kaefer-medium-grey">
							{selected.length}
						</span>
					)}
				</div>
				<ChevronDown
					className={cn(
						'size-4 text-kaefer-medium-grey transition-transform duration-200',
						open && 'rotate-180',
					)}
				/>
			</button>

			{open && (
				<div className="flex flex-col gap-3 p-3 border-t border-kaefer-light-silver/40">
					{crs.length === 0 ? (
						<p className="text-sm text-muted-foreground text-center py-2">
							{t('administration.contracts.form.noCRsForBranch')}
						</p>
					) : (
						<>
							{/* Search + select all row */}
							<div className="flex items-center gap-2">
								<div className="relative flex-1">
									<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-kaefer-medium-grey/60 pointer-events-none" />
									<Input
										value={search}
										onChange={(e) => setSearch(e.target.value)}
										placeholder={t(
											'administration.contracts.form.crSearchPlaceholder',
										)}
										className="h-8 pl-8 text-sm"
									/>
								</div>
								<label className="flex shrink-0 cursor-pointer items-center gap-2 rounded-md border border-kaefer-light-silver/60 bg-background px-3 py-1.5 text-sm font-medium text-kaefer-dark-grey hover:bg-muted/40 transition-colors">
									<input
										type="checkbox"
										checked={allSelected}
										onChange={handleToggleAll}
										className="size-3.5 accent-kaefer-medium-grey"
									/>
									{allSelected
										? t('administration.questionnairesForm.deselectAll')
										: t('administration.questionnairesForm.selectAllBranches')}
								</label>
							</div>

							{/* CR list */}
							<div className="flex flex-col divide-y divide-kaefer-light-silver/30 max-h-56 overflow-y-auto rounded-md border border-kaefer-light-silver/40">
								{filtered.length === 0 ? (
									<p className="py-3 text-center text-sm text-muted-foreground">
										{t('administration.questionnairesForm.noBranchesFound')}
									</p>
								) : (
									filtered.map((cr) => {
										const isUsed =
											usedCrIds.has(cr.id) && !selected.includes(cr.id)
										return (
											<label
												key={cr.id}
												className={cn(
													'flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm transition-colors',
													isUsed
														? 'opacity-60 cursor-not-allowed bg-muted/20'
														: 'hover:bg-muted/30',
												)}
											>
												<input
													type="checkbox"
													checked={selected.includes(cr.id)}
													onChange={() => !isUsed && handleToggle(cr.id)}
													disabled={isUsed}
													className="size-3.5 accent-kaefer-medium-grey"
												/>
												<div className="flex flex-col min-w-0">
													<span className="truncate font-medium text-kaefer-dark-grey">
														{cr.name}
													</span>
													{cr.code && (
														<span className="text-[11px] text-muted-foreground">
															{cr.code}
														</span>
													)}
												</div>
												{isUsed && (
													<span className="ml-auto shrink-0 text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
														{t('administration.contracts.form.crUsed')}
													</span>
												)}
											</label>
										)
									})
								)}
							</div>
						</>
					)}
				</div>
			)}
		</div>
	)
}
